// ============================================================
// MySchedulePage — 員工專用：只顯示自己的排班
// ============================================================

import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../lib/api';
import { useAuthStore } from '../stores/authStore';
import { ErrorBanner } from './ErrorBanner';

interface Schedule {
  id: string;
  date: string;
  startTime: string;
  endTime: string;
  breakStart?: string;
  breakEnd?: string;
  position?: string;
  isPublished: boolean;
  user?: { id: string; name: string };
  template?: { id: string; name: string; color: string };
}

// 注意：用本地時間轉 YYYY-MM-DD，避免 toISOString() 把台灣時間 (UTC+8) 轉成 UTC 後日期回退一天
function toDateStr(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function startOfWeek(d: Date): Date {
  const day = d.getDay();
  const offset = day === 0 ? -6 : 1 - day;
  const r = new Date(d);
  r.setDate(r.getDate() + offset);
  r.setHours(0, 0, 0, 0);
  return r;
}

const WEEKDAY = ['日', '一', '二', '三', '四', '五', '六'];

export function MySchedulePage() {
  const { user } = useAuthStore();
  const [weekStart, setWeekStart] = useState<Date>(startOfWeek(new Date()));
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchMy = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const ws = toDateStr(weekStart);
      const res = await api.get<{ schedules: Schedule[] }>(`/schedules?weekStart=${ws}`);
      // 只顯示自己的班（未發佈的會用 (未發佈) badge 標示）
      const mine = res.schedules.filter(s => s.user?.id === user.id);
      setSchedules(mine);
      setError(null);
    } catch (err) {
      // 不可吞掉：失敗時七天會全部顯示「休息」，員工會以為自己真的沒班。
      setError((err as Error).message);
    }
    finally { setLoading(false); }
  }, [user, weekStart]);

  useEffect(() => { fetchMy(); }, [fetchMy]);

  const weekDates: Date[] = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    weekDates.push(d);
  }

  const scheduleByDate = new Map<string, Schedule[]>();
  for (const s of schedules) {
    const dateStr = s.date.slice(0, 10);
    if (!scheduleByDate.has(dateStr)) scheduleByDate.set(dateStr, []);
    scheduleByDate.get(dateStr)!.push(s);
  }

  const weeklyHours = schedules.reduce((sum, s) => {
    const [sh, sm] = s.startTime.split(':').map(Number);
    const [eh, em] = s.endTime.split(':').map(Number);
    let h = (eh * 60 + em - sh * 60 - sm) / 60;
    if (s.breakStart && s.breakEnd) {
      const [bsh, bsm] = s.breakStart.split(':').map(Number);
      const [beh, bem] = s.breakEnd.split(':').map(Number);
      h -= (beh * 60 + bem - bsh * 60 - bsm) / 60;
    }
    return sum + Math.max(0, h);
  }, 0);

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 space-y-5">
      {error && <ErrorBanner message={error} onRetry={fetchMy} />}

      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-morandi-800">我的班表</h1>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setWeekStart(d => { const n = new Date(d); n.setDate(n.getDate() - 7); return n; })}
            className="w-8 h-8 rounded-full text-morandi-500 hover:bg-morandi-100 flex items-center justify-center"
          >‹</button>
          <span className="text-sm font-medium text-morandi-700 min-w-[120px] text-center">
            {toDateStr(weekStart)} ~ {toDateStr(weekDates[6])}
          </span>
          <button
            onClick={() => setWeekStart(d => { const n = new Date(d); n.setDate(n.getDate() + 7); return n; })}
            className="w-8 h-8 rounded-full text-morandi-500 hover:bg-morandi-100 flex items-center justify-center"
          >›</button>
        </div>
      </div>

      <div className="card-morandi p-4">
        <div className="text-sm text-morandi-500 mb-1">本週總工時</div>
        <div className="text-2xl font-bold font-mono text-morandi-700">
          {Math.round(weeklyHours * 10) / 10} <span className="text-sm font-normal text-morandi-400">hr</span>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-10 text-morandi-400">載入中...</div>
      ) : (
        <div className="space-y-2">
          {weekDates.map(date => {
            const dateStr = toDateStr(date);
            const items = scheduleByDate.get(dateStr) ?? [];
            const isToday = dateStr === toDateStr(new Date());
            return (
              <div
                key={dateStr}
                className={`card-morandi p-4 ${isToday ? 'ring-2 ring-earth-400' : ''}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <span className="text-sm font-medium text-morandi-700">
                      {date.getMonth() + 1}/{date.getDate()}
                    </span>
                    <span className="ml-2 text-xs text-morandi-400">週{WEEKDAY[date.getDay()]}</span>
                    {isToday && <span className="ml-2 text-xs text-earth-500">今天</span>}
                  </div>
                </div>
                {items.length === 0 ? (
                  <div className="text-sm text-morandi-300">休息</div>
                ) : (
                  <div className="space-y-1">
                    {items.map(s => (
                      <div
                        key={s.id}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg"
                        style={{ backgroundColor: (s.template?.color ?? '#8B9DAF') + '15' }}
                      >
                        <div
                          className="w-1 h-6 rounded-full"
                          style={{ backgroundColor: s.template?.color ?? '#8B9DAF' }}
                        />
                        <span className="text-sm font-medium text-morandi-700">
                          {s.template?.name ?? '自訂'}
                        </span>
                        <span className="text-sm font-mono text-morandi-500">
                          {s.startTime}–{s.endTime}
                        </span>
                        {s.position && (
                          <span className="text-xs text-morandi-400">@ {s.position}</span>
                        )}
                        {!s.isPublished && (
                          <span className="text-xs text-amber-500 ml-auto">(未發佈)</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
